'use client'

import { useRouter } from 'next/navigation'
import { parseCookies } from 'nookies'
import type { ReactNode } from 'react'
import { useEffect, useMemo, useState } from 'react'

import configuration from '~/configuration'

import { useRouterContext } from '~/lib/next/use-router-context'

import useSubscriptionPlan from '~/components/Subscription/use-subscription-plan'

import { destroySessionCookiesClient } from '~/core/utilities/catch-api-error'
import type { redirectTo } from '~/lib/next/authUtilities'
import { handleAuthenticationInternalFlow } from '~/lib/next/authUtilities'
import { hasCompleteInternalSession } from '~/lib/next/internalSessionCookies'
import useBoundStore from '~/lib/store'
import {
  getSlugTenantFromCareerHubUrl,
  isCPageUrl,
  isProtectedRoute
} from '~/lib/utilities/pathname'

interface InternalProviderProps {
  children: ReactNode
}

function InternalFlowProvider({ children }: InternalProviderProps) {
  const { params, searchParams, pathnameWithoutLocale, asPathWithoutLocale } = useRouterContext()
  const router = useRouter()
  const { user } = useBoundStore()
  const { data: subscriptionData } = useSubscriptionPlan()

  const [isLoading, setIsLoading] = useState(true)

  // Check if current route is protected
  const isPrivateRoute = useMemo(() => {
    return isProtectedRoute(asPathWithoutLocale)
  }, [asPathWithoutLocale])

  // Main authentication effect
  useEffect(() => {
    if (!searchParams || !params || !pathnameWithoutLocale) return

    const authenticationMiddleware = async () => {
      try {
        // For public routes, show component immediately
        if (!isPrivateRoute) {
          setIsLoading(false)
          return
        }

        const cookies = parseCookies()

        // For private routes, run authentication flow
        if (isPrivateRoute && !hasCompleteInternalSession(cookies)) {
          destroySessionCookiesClient(window.location.pathname)
          window.location.href = configuration.path.login
          return
        }

        const planInfo = subscriptionData && {
          isExpired: subscriptionData.isExpired,
          defaultPlan: subscriptionData.defaultPlan
        }

        const tenantSlug = getSlugTenantFromCareerHubUrl(asPathWithoutLocale)
        // Run authentication flow
        const flowResult = handleAuthenticationInternalFlow({
          cookies,
          user,
          resolvedUrl: asPathWithoutLocale,
          query: params,
          planInfo
        })

        const redirectResponse = flowResult as ReturnType<typeof redirectTo>

        // Handle redirects
        if (redirectResponse.redirect) {
          // Pages that should not redirect (CareerHub login/register)
          const pagesNotRedirect = tenantSlug
            ? [
                configuration.path.careerHub.login(tenantSlug),
                configuration.path.careerHub.register(tenantSlug)
              ]
            : []

          const redirectSearch = pagesNotRedirect.filter(
            (item) => asPathWithoutLocale?.search(item) !== -1
          )

          // Only redirect if not on no-redirect pages
          if (redirectSearch.length === 0) {
            const redirectDestination = String(redirectResponse.redirect.destination)

            // Determine redirect method
            const PAGES_USE_WINDOW_LOCATION: string[] = [configuration.path.login]
            const useWindowLocation = PAGES_USE_WINDOW_LOCATION.some((url) =>
              redirectDestination.startsWith(url)
            )
            const isRedirectingToLogin = redirectDestination.startsWith(configuration.path.login)

            if (isRedirectingToLogin) {
              destroySessionCookiesClient(window.location.pathname)
            }
            if (useWindowLocation || isRedirectingToLogin) {
              window.location.href = redirectDestination
            } else {
              router.push(redirectDestination)
            }
            return
          }
        }

        setIsLoading(false)
      } catch (error) {
        setIsLoading(false)
      }
    }

    authenticationMiddleware()
  }, [
    subscriptionData,
    isPrivateRoute,
    searchParams,
    params,
    pathnameWithoutLocale,
    asPathWithoutLocale,
    user,
    router
  ])

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return

      const cookies = parseCookies()
      const pathname = window.location.pathname
      const isPrivateRoute = isProtectedRoute(pathname)
      const hasValidAuthState = hasCompleteInternalSession(cookies)
      const isStateLoggedIn = hasValidAuthState && !isPrivateRoute
      const isStateLoggedOut = !hasValidAuthState && isPrivateRoute
      if (
        document.visibilityState === 'visible' &&
        (isStateLoggedIn || isStateLoggedOut) &&
        !isCPageUrl(pathname)
      ) {
        if (isStateLoggedOut) {
          destroySessionCookiesClient(pathname)
          window.location.href = configuration.path.login
          return
        }
        window.location.href = configuration.path.default
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  // Show loading state for private routes during auth check
  if (isLoading && isPrivateRoute) {
    return <></>
  }

  return children
}

export default InternalFlowProvider
